import { CircularProgress, Divider, Grid, InputAdornment } from '@material-ui/core';
import { ArrowForward } from '@material-ui/icons';
import { Button, Dialog, Icons, TextField, Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import { deficiencyAudit, taskAudit } from '../../graphql';
import { useMst } from '../../mobx-models/Root';
import { isJestTest } from '../../test-helpers/isJestTest';
import { mockAuditTaskData, mockDeficiencyAuditData } from '../../test-helpers/mockData/auditLogs';
import { useNotification } from '../../utilities/useNotification';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { useWindowDimensions } from '../../utilities/useWindowDimensions';
import { YYYYMMDD_DATE_FORMAT } from '../../utilities/constants';
import { Discipline } from '../../utilities/enums';
import { trackEventFn, TrackingEventType } from '../../utilities/hooks/useEventTracking';
import ActiveDeficienciesAuditLogs from '../ActiveDeficienciesAuditLogs';
import CustomAvatarIcon from '../AppBar/CustomAvatarIcon';
import useStyles from './AuditLogs.styles';
import { IAuditLogData } from './AuditLogs.types';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subTitle: string;
  deficiencyId: number;
  isUpdateHistory: boolean;
  isActiveDeficiency: boolean;
  inspectionIdForActiveDeficiency?: number;
}

const AuditLogs: React.FC<Props> = ({
  open,
  onClose,
  title,
  subTitle,
  deficiencyId,
  isUpdateHistory,
  isActiveDeficiency,
  inspectionIdForActiveDeficiency,
}) => {
  const classes = useStyles();
  const { CurrentUserSelected, SiteDiscipline } = useMst();
  const size = useWindowDimensions();
  const { errorNotification } = useNotification();
  const online = useOnlineStatus();
  const [searchText, setSearchText] = useState('');
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [auditData, setAuditData] = useState<IAuditLogData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortAscending, setSortAscending] = useState(false);
  const taskId = CurrentUserSelected.taskId;
  const inspectionId = CurrentUserSelected.inspectionId;
  const offlineTextMsg = 'User Need To Connected With Internet To See This Data.';
  const subTitleForHeader = isUpdateHistory ? 'Update History' : 'Audit Logs';
  const isElectrical = SiteDiscipline.disciplineId === Discipline.Electrical;
  let header =
    isUpdateHistory || isActiveDeficiency
      ? `${subTitleForHeader} - Deficiency ID ${deficiencyId} ${
          inspectionIdForActiveDeficiency !== 0 && inspectionIdForActiveDeficiency !== null
            ? `(Inspection ID ${inspectionIdForActiveDeficiency})`
            : ''
        }`
      : `Audit Logs - ${title} ID ${deficiencyId || taskId} (${subTitle}  ID ${
          deficiencyId ? taskId : inspectionId
        })`;

  if (isElectrical) {
    header = header.replace('Inspection', 'PM');
  }

  useEffect(() => {
    let isMounted = true;
    const getAuditData = async () => {
      setLoading(true);
      try {
        let result;
        if (deficiencyId) {
          result = isJestTest()
            ? mockDeficiencyAuditData
            : await deficiencyAudit(deficiencyId, inspectionId);
          if (isMounted) {
            if (result.errors && isMounted) {
              throw new Error(`error ${result.errors}`);
            }
            setAuditData(result.data.deficiencyAuditInfoById);
          }
        } else if (taskId) {
          result = isJestTest() ? mockAuditTaskData : await taskAudit(taskId);
          if (isMounted) {
            if (result.errors && isMounted) {
              throw new Error(`error ${result.errors}`);
            }
            setAuditData(result.data.taskAuditInfoById);
          }
        }
      } catch (error) {
        if (isMounted) {
          errorNotification('Failed to load data');
          await trackEventFn(TrackingEventType.AUDIT_LOG_FAILED, {
            deficiencyId,
            taskId,
            error: JSON.stringify(error),
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    if (online) getAuditData();
    return () => {
      isMounted = false;
    };
  }, [deficiencyId, taskId, online]);

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      setSearchText('');
      setSearchBarOpen(false);
    }
    return () => {
      isMounted = false;
    };
  }, [size.height > size.width]);

  const handleSearch = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchText(value);
  };

  const getDate = (date: string) => {
    if (!date) return '';
    const DATE = new Date(date);
    DATE.setHours(DATE.getHours() + 6);
    const dateToDisplay = dayjs(DATE).format(`${YYYYMMDD_DATE_FORMAT} HH:mm:ss`);
    return dateToDisplay;
  };

  const checkOldAndNewValue = (audit: IAuditLogData) => {
    return (
      audit?.oldValue !== audit?.newValue &&
      !(audit?.oldValue === null && audit?.newValue === '') &&
      !(audit?.oldValue === '' && audit?.newValue === null)
    );
  };

  const showData = (audit: IAuditLogData) => {
    return (
      (audit?.userName?.toLowerCase().includes(searchText?.toLowerCase()) ||
        audit?.oldValue?.toLowerCase().includes(searchText?.toLowerCase()) ||
        audit?.newValue?.toLowerCase().includes(searchText?.toLowerCase()) ||
        audit?.userRole?.toLowerCase().includes(searchText?.toLowerCase()) ||
        audit?.field?.toLowerCase().includes(searchText?.toLowerCase()) ||
        audit?.actionDate?.includes(searchText) ||
        audit?.action?.toLowerCase().includes(searchText?.toLowerCase())) &&
      ((!isUpdateHistory && checkOldAndNewValue(audit)) || isUpdateHistory)
    );
  };

  const handleSort = () => {
    setSortAscending(!sortAscending);
  };

  const getValue = (obj: IAuditLogData, item: string) => {
    return obj && obj[item as keyof IAuditLogData] && obj[item as keyof IAuditLogData]?.length
      ? obj[item as keyof IAuditLogData]
      : 'None';
  };

  const sortBasedOnDate = (a: IAuditLogData, b: IAuditLogData) => {
    const firstDate = new Date(a.actionDate);
    const secondDate = new Date(b.actionDate);
    if (firstDate < secondDate) {
      return sortAscending ? -1 : 1;
    }
    if (firstDate === secondDate) {
      return '0';
    }
    return sortAscending ? 1 : -1;
  };

  return (
    <Dialog
      data-testid="audit-logs"
      open={open}
      onClose={onClose}
      titleText={header}
      backButton={false}
      fullScreen
      dialogActions={
        <Grid
          container
          justifyContent="flex-end"
          alignItems="center"
          className={classes.okButtonGrid}
        >
          <Grid item>
            <Button
              noMinHeight
              color="primary"
              variant="contained"
              onClick={onClose}
              data-testid="okbutton"
            >
              <Typography fontWeight={700}>Ok</Typography>
            </Button>
          </Grid>
        </Grid>
      }
      maxWidth="lg"
    >
      {!online && auditData?.length === 0 ? (
        <Grid
          item
          container
          alignContent="center"
          justifyContent="center"
          className={classes.offline}
        >
          {offlineTextMsg}
        </Grid>
      ) : (
        <>
          <Grid className={classes.topGrid1}>
            <Grid item container xs={12} className={classes.topGrid}>
              {searchBarOpen ? (
                <Grid xs={12} item className={classes.searchBarOuterGrid1} alignItems="center">
                  <TextField
                    fullWidth
                    noMinHeight
                    focused
                    variant="outlined"
                    size="small"
                    startAdornment={
                      <InputAdornment position="start">
                        <Icons.SearchFeather color="primary" />
                      </InputAdornment>
                    }
                    className="searchBarBg"
                    placeholder="Search"
                    onChange={handleSearch}
                    data-testid="seachbox"
                  />
                  <Grid item className={classes.xFeatherGrid}>
                    <Icons.XFeather
                      color="primary"
                      onClick={() => {
                        setSearchText('');
                        setSearchBarOpen(false);
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <>
                  <Grid item xs={size.height > size.width ? 8 : 6} />
                  <Grid item xs={3} justifyContent="flex-end" className={classes.oldestGrid}>
                    {!isUpdateHistory && (
                      <>
                        <Typography
                          color="primary"
                          className="oldest"
                          fontWeight={700}
                          onClick={handleSort}
                          data-testid="oldestFirstButton"
                        >
                          Oldest First
                        </Typography>
                        {!sortAscending ? (
                          <Icons.ArrowDownFeather
                            color="primary"
                            className="icon"
                            onClick={handleSort}
                          />
                        ) : (
                          <Icons.ArrowUpFeather
                            color="primary"
                            className="icon"
                            onClick={handleSort}
                          />
                        )}
                        <Divider orientation="vertical" className={classes.dividerGrid} />
                      </>
                    )}
                  </Grid>
                  {size.height < size.width ? (
                    <Grid item xs={3} className={classes.searchBarOuterGrid} alignItems="center">
                      <TextField
                        fullWidth
                        noMinHeight
                        focused
                        variant="outlined"
                        size="small"
                        startAdornment={
                          <InputAdornment position="start">
                            <Icons.SearchFeather color="primary" />
                          </InputAdornment>
                        }
                        className="searchBarBg"
                        placeholder="Search"
                        onChange={handleSearch}
                        data-testid="seachbox"
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={1} className={classes.searchBarOuterGrid} alignItems="center">
                      <Icons.SearchFeather color="primary" onClick={() => setSearchBarOpen(true)} />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Grid>

          {loading && auditData?.length === 0 ? (
            <Grid item xs={12} className={classes.circular}>
              <CircularProgress color="primary" />
            </Grid>
          ) : isUpdateHistory ? (
            <ActiveDeficienciesAuditLogs
              auditData={auditData}
              getDate={getDate}
              sortBasedOnDate={sortBasedOnDate}
              getValue={getValue}
              showData={showData}
              searchText={searchText}
            />
          ) : (
            auditData
              ?.filter(showData)
              ?.sort((a: IAuditLogData, b: IAuditLogData) => {
                return sortBasedOnDate(a, b);
              })
              .map((item: IAuditLogData, index: number) => {
                return (
                  <Grid className={classes.card1} key={index}>
                    <Grid item container xs={12} className={classes.iconGrid}>
                      <Grid item xs={4} className={classes.grid1}>
                        <Grid className={classes.userIcon}>
                          <CustomAvatarIcon />
                        </Grid>
                        <Grid className={classes.nameGrid}>
                          <Typography fontWeight={600} variant="subtitle1">
                            {item?.userName}
                          </Typography>
                          <Typography variant="body1">{item?.userRole}</Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={5} className={classes.descriptionGrid}>
                        <Typography variant="subtitle1" color="primary">
                          {item?.field}
                          <span className={classes.subtitle}>{item?.action}</span>
                        </Typography>
                        <Grid className={classes.detailsGrid}>
                          {!(
                            item?.field?.toLowerCase() === 'deficiency' &&
                            item?.action?.toLowerCase() === 'updated'
                          ) && (
                            <>
                              <Typography className={classes.none}>
                                {getValue(item, 'oldValue')}
                              </Typography>
                              <ArrowForward className={classes.ArrowForwardStyle} />
                            </>
                          )}
                          <Typography variant="body1" className={classes.fileName}>
                            {getValue(item, 'newValue')}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography
                          variant="subtitle1"
                          className={classes.timeStamp}
                          data-testid="datefield"
                        >
                          {getDate(item?.actionDate)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })
          )}
        </>
      )}
    </Dialog>
  );
};

export default observer(AuditLogs);
